import { Currency } from '@/constants/Currency';
import { IrFutureConvention } from '@/constants/IrFutureConventionConstants';
import { useLocalStorage, type RemovableRef } from '@vueuse/core';
import type { ComputedRef, Ref } from 'vue';
import { computed, watch } from 'vue';
import { MetricConstants } from '@/constants/MetricConstants';
import type { StressTestDropdownType, HistoryGroupBy, CustomVaR } from '@/types/analytics/ConstituentRisk';
import {
  CommodityFilterConstants,
  ExposureConstants,
  FilterConstants,
  EquityGroupByConstants,
  LadderTitles,
  RiskTableTabs,
  RiskTabs,
  TableLayerConstants,
  UnitConstants,
  VarGrouping,
  VarMetrics,
  VarNotional,
  customTests,
  historyTestOption,
  CustomVaRType,
  CustomVaRScenarioTenor,
  CustomVaRLookbackPeriod,
  CustomVaRStressedPeriod,
} from '@/types/analytics/ConstituentRisk';
import { LocalStorageEntities } from '@/constants/LocalStorageEntities';

export type PreferenceOptions = {
  unit: UnitConstants;
  display: ExposureConstants;
  basisPoints: number;
  var: VarMetrics;
  customVaR: CustomVaR;
  'stress-test': string;
  equity: MetricConstants;
  'interest-rate': string;
  credit: string;
  fx: MetricConstants;
  commodity: MetricConstants;
  trackSegment: FilterConstants.PORTFOLIO;
  tableLayers: TableLayerConstants[];
  assetRiskCols: string[];
  customRiskCols: string[];
  filterNullQuantityPosition: boolean;
  applyQisOptionDelta: boolean;
  irFutureConvention: IrFutureConvention;
};

export type VarPreference = {
  metric: VarMetrics;
  category: FilterConstants;
  grouping: VarGrouping;
  metric2: VarNotional;
  category2: FilterConstants;
  display: ExposureConstants;
  historyCategory: FilterConstants | HistoryGroupBy;
  tableMetricName: RiskTableTabs;
  exposureRiskCols: string[];
  // WAA-14288 - Decided to not remove this, so that a user will not lose their local storage preferences
  factorRiskCols: string[];
  varRiskCols: string[];
};

export type StressTestPreference = {
  category: FilterConstants;
  filter: FilterConstants;
  filterApplied: string;
  testOptions: StressTestDropdownType[];
  historyTestOption: StressTestDropdownType;
  historyCategory: FilterConstants | HistoryGroupBy;
  tableMetricName: RiskTableTabs;
  transitiveStressRiskCols: string[];
  historicalStressRiskCols: string[];
};

export type LadderPreference = {
  ladder1: LadderTitles;
  ladder2: LadderTitles;
};
export type EquityPreference = {
  metric: MetricConstants;
  display: ExposureConstants;
  filterLevel1: EquityGroupByConstants | FilterConstants;
  filterLevel2: EquityGroupByConstants | FilterConstants;
  /**
   * Instrument is a bit special. This is one preference that we will clear regularly.
   * If instrument is populated, we will show the drilled down graph for the given sensitivity.
   * If it's not populated, then we show the 'overview' graph.
   */
  instrument: string;
  historyCategory: FilterConstants;
  tableMetricName: RiskTableTabs;
  ladderTableLayers: TableLayerConstants[];
  sensitivityRiskCols: string[];
  ladderRiskCols: string[];
} & LadderPreference;

export type InterestRatePreference = {
  metric: MetricConstants;
  display: ExposureConstants;
  instrument: Currency | 'All';
  category: FilterConstants;
  historyCategory: FilterConstants;
  tableMetricName: RiskTableTabs;
  tableLayers: TableLayerConstants[];
  ladderTableLayers: TableLayerConstants[];
  sensitivityRiskCols: string[];
  ladderRiskCols: string[];
} & LadderPreference;

export type CreditPreference = {
  display: ExposureConstants;
  instrument: string;
  category: FilterConstants;
  historyCategory: FilterConstants;
  tableMetricName: RiskTableTabs;
  tableLayers: TableLayerConstants[];
  sensitivityRiskCols: string[];
};

export type FxPreference = {
  metric: MetricConstants;
  instrument: string;
  display: ExposureConstants;
  category: FilterConstants;
  filter: FilterConstants;
  historyCategory: FilterConstants;
  tableMetricName: RiskTableTabs;
  tableLayers: TableLayerConstants[];
  ladderTableLayers: TableLayerConstants[];
  sensitivityRiskCols: string[];
  ladderRiskCols: string[];
} & LadderPreference;

export type CommodityPreference = {
  metric: MetricConstants;
  filter: CommodityFilterConstants;
  display: ExposureConstants;
  instrument: string;
  tableLayers: TableLayerConstants[];
  category: FilterConstants | CommodityFilterConstants;
  historyCategory: FilterConstants | HistoryGroupBy;
  tableMetricName: RiskTableTabs;
  ladderTableLayers: TableLayerConstants[];
  sensitivityRiskCols: string[];
  ladderRiskCols: string[];
} & LadderPreference;

export type PreferenceStorage = {
  /**
   * Mostly consists of parameters used by the ribbon, parameters modal, and other global settings
   */
  options: PreferenceOptions;
  [RiskTabs.VAR]: VarPreference;
  [RiskTabs.STRESS_TEST]: StressTestPreference;
  [RiskTabs.EQUITY]: EquityPreference;
  [RiskTabs.INTEREST_RATE]: InterestRatePreference;
  [RiskTabs.CREDIT]: CreditPreference;
  [RiskTabs.FX]: FxPreference;
  [RiskTabs.COMMODITY]: CommodityPreference;
};

/**
 * Checks that the default preferences object keys match with that of the saved preferences
 */
export const compareKeys = (
  defaultPreferences: Record<string, unknown>,
  savedPreferences: Record<string, unknown>,
): boolean => {
  const defaultKeys = Object.keys(defaultPreferences).sort();
  const savedKeys = Object.keys(savedPreferences).sort();
  return JSON.stringify(defaultKeys) === JSON.stringify(savedKeys);
};

export const defaultAssetCols = [
  'Strategy',
  'Asset Class',
  'Product Type',
  'Underlying',
  'Position Date',
  'Currency',
  'Foreign Currency',
  'Start Date',
  'Maturity',
  'Strike',
  'Instrument ID',
];

export const defaultExposureCols = [
  'Value',
  'Exposure Notional Net',
  'Exposure Notional Gross',
  'Equivalent Delta Notional',
];

export const defaultTransitiveStressCols = [
  'MSCI World -10%',
  'S&P 500 -10%',
  'VIX +20 Points',
  'IR G3 +50 bps',
  'IR G3 Steepening 2/10 (-20/20 bps)',
  'CCY G10 vs USD -5%',
  'MXWO -10%, IR USD, EUR +75 bps',
  'BCOMTR +10%',
];

export const defaultEquitySensitivityCols = [
  'EQ.Delta',
  'EQ.Gamma',
  'EQ.Theta',
  'EQ.Vega',
  'VOL.Delta',
  'VOL.Gamma',
  'VOL.Vega',
];

export const defaultInterestRateSensitivityCols = [
  'IR.DV01(1bp)',
  'IR.Theta',
  'IR.Vega(1%) BS',
  'IR.Vega(1bp) normal',
  'IR Steepening 2/10 (-20/20 bps)',
];

export const defaultCreditSensitivityCols = [
  'CR.CS01(1bp)',
  'CR.Theta',
  'CR Steepening 2/10 (-20/20 bps)',
  'Duration Times Spread (DTS)',
];

export const defaultFXSensitivityCols = ['FX.Delta[USD]', 'FX.Gamma', 'FX.Theta', 'FX.Vega'];

export const defaultCommoditySensitivityCols = ['CM.Delta', 'CM.Gamma', 'CM.Theta', 'CM.Vega'];

export type InstrumentTabs =
  | RiskTabs.EQUITY
  | RiskTabs.INTEREST_RATE
  | RiskTabs.CREDIT
  | RiskTabs.FX
  | RiskTabs.COMMODITY;

export const defaultInstruments = {
  [RiskTabs.EQUITY]: 'All',
  [RiskTabs.INTEREST_RATE]: Currency.USD,
  [RiskTabs.CREDIT]: 'US',
  [RiskTabs.FX]: 'All',
  [RiskTabs.COMMODITY]: 'Brent Crude Oil',
};

// Update the LocalStorageEntities.RISK_PREFERENCES version when defaultPreferences changes
const defaultPreferences: PreferenceStorage = {
  options: {
    unit: UnitConstants.CURRENCY,
    display: ExposureConstants.NET,
    basisPoints: 1,
    var: VarMetrics.CUSTOM_VAR,
    customVaR: {
      type: CustomVaRType.H_VAR,
      scenarioTenor: CustomVaRScenarioTenor.FIVE_DAY_OVERLAP,
      lookbackPeriod: CustomVaRLookbackPeriod.THREE_YEAR,
      stressedPeriod: CustomVaRStressedPeriod.FINANCIAL_CRISIS_2008,
      confidenceLevel: 0.99,
      horizon: 20,
      decay: 1,
    },
    'stress-test': 'S&P 500 -10%',
    equity: MetricConstants.DELTA,
    'interest-rate': `DV01 (+1 bp)`,
    credit: `CS01 (+1 bp)`,
    fx: MetricConstants.DELTA,
    commodity: MetricConstants.DELTA,
    trackSegment: FilterConstants.PORTFOLIO,
    tableLayers: [TableLayerConstants.STRATEGY, TableLayerConstants.PRODUCT_TYPE, TableLayerConstants.INSTRUMENT],
    assetRiskCols: defaultAssetCols,
    customRiskCols: [],
    filterNullQuantityPosition: true,
    applyQisOptionDelta: true,
    irFutureConvention: IrFutureConvention.TickSize,
  },
  [RiskTabs.VAR]: {
    metric: VarMetrics.CVAR_95,
    metric2: VarNotional.EXPOSURE_NOTIONAL,
    category: FilterConstants.RISK_CURRENCY,
    grouping: VarGrouping.ALLOCATED,
    category2: FilterConstants.FACTOR,
    display: ExposureConstants.LONG,
    historyCategory: FilterConstants.STRATEGY,
    tableMetricName: RiskTableTabs.VAR,
    exposureRiskCols: defaultExposureCols,
    factorRiskCols: [],
    varRiskCols: [],
  },
  [RiskTabs.STRESS_TEST]: {
    category: FilterConstants.FACTOR,
    filter: FilterConstants.FACTOR,
    filterApplied: 'All',
    testOptions: customTests,
    historyTestOption,
    historyCategory: FilterConstants.STRATEGY,
    tableMetricName: RiskTableTabs.TRANSITIVE_STRESS,
    transitiveStressRiskCols: defaultTransitiveStressCols,
    historicalStressRiskCols: [],
  },
  [RiskTabs.EQUITY]: {
    metric: MetricConstants.DELTA,
    ladder1: LadderTitles.SPOT,
    ladder2: LadderTitles.VOL,
    display: ExposureConstants.LONG,
    filterLevel1: FilterConstants.COUNTRY,
    filterLevel2: EquityGroupByConstants.SECTOR_LEVEL_1,
    instrument: 'All',
    historyCategory: FilterConstants.STRATEGY,
    tableMetricName: RiskTableTabs.SENSITIVITY,
    ladderTableLayers: [TableLayerConstants.VOLATILITY_SHOCK, TableLayerConstants.INSTRUMENT],
    sensitivityRiskCols: defaultEquitySensitivityCols,
    ladderRiskCols: [],
  },
  [RiskTabs.INTEREST_RATE]: {
    metric: MetricConstants.DELTA,
    display: ExposureConstants.LONG,
    instrument: 'All',
    ladder1: LadderTitles.SPOT,
    ladder2: LadderTitles.VOL,
    category: FilterConstants.RISK_CURRENCY,
    historyCategory: FilterConstants.STRATEGY,
    tableMetricName: RiskTableTabs.SENSITIVITY,
    tableLayers: [TableLayerConstants.RISK_CURRENCY],
    ladderTableLayers: [
      TableLayerConstants.VOLATILITY_SHOCK,
      TableLayerConstants.RISK_CURRENCY,
      TableLayerConstants.INSTRUMENT,
    ],
    sensitivityRiskCols: defaultInterestRateSensitivityCols,
    ladderRiskCols: [],
  },
  [RiskTabs.CREDIT]: {
    display: ExposureConstants.LONG,
    instrument: 'US',
    category: FilterConstants.COUNTRY,
    historyCategory: FilterConstants.STRATEGY,
    tableMetricName: RiskTableTabs.SENSITIVITY,
    tableLayers: [TableLayerConstants.COUNTRY],
    sensitivityRiskCols: defaultCreditSensitivityCols,
  },
  [RiskTabs.FX]: {
    metric: MetricConstants.DELTA,
    display: ExposureConstants.LONG,
    category: FilterConstants.FACTOR,
    filter: FilterConstants.FACTOR,
    ladder1: LadderTitles.SPOT,
    ladder2: LadderTitles.VOL,
    instrument: 'All',
    historyCategory: FilterConstants.STRATEGY,
    tableMetricName: RiskTableTabs.SENSITIVITY,
    tableLayers: [TableLayerConstants.RISK_CURRENCY],
    ladderTableLayers: [TableLayerConstants.STRATEGY, TableLayerConstants.PRODUCT_TYPE, TableLayerConstants.INSTRUMENT],
    sensitivityRiskCols: defaultFXSensitivityCols,
    ladderRiskCols: [],
  },
  [RiskTabs.COMMODITY]: {
    metric: MetricConstants.DELTA,
    ladder1: LadderTitles.SPOT,
    ladder2: LadderTitles.VOL,
    filter: CommodityFilterConstants.COMMODITY,
    display: ExposureConstants.LONG,
    instrument: 'Brent Crude Oil',
    tableLayers: [TableLayerConstants.SECTOR, TableLayerConstants.COMMODITY],
    category: CommodityFilterConstants.COMMODITY,
    historyCategory: FilterConstants.STRATEGY,
    tableMetricName: RiskTableTabs.COMMODITY_DELTA,
    ladderTableLayers: [TableLayerConstants.SECTOR, TableLayerConstants.COMMODITY],
    sensitivityRiskCols: defaultCommoditySensitivityCols,
    ladderRiskCols: [],
  },
};

export function useRiskPreferences(initialParams?: PreferenceStorage): {
  preferences: RemovableRef<PreferenceStorage>;
  varMetric1: ComputedRef<string>;
} {
  const initialState = initialParams ?? defaultPreferences;

  /**
   * If constituent risk preferences exist, replace the default preferences
   */
  const preferences = useLocalStorage<PreferenceStorage>(LocalStorageEntities.RISK_PREFERENCES, initialState, {
    mergeDefaults: true,
  });

  const varMetric1 = computed(() => {
    if (preferences.value.var.metric === VarMetrics.CUSTOM_VAR)
      return `${preferences.value.var.metric} ${preferences.value.var.grouping}`;

    return `Historical ${preferences.value.var.metric} ${preferences.value.var.grouping}`;
  });

  return {
    preferences,
    varMetric1,
  };
}

export function useValidateInstrument(tab: InstrumentTabs, valuesToCheck: Ref<string[]>): void {
  const { preferences } = useRiskPreferences();

  // eslint-disable-next-line no-restricted-syntax
  watch(
    () => preferences.value[tab].instrument,
    (newVal) => {
      if (!valuesToCheck.value.includes(newVal)) {
        preferences.value[tab].instrument = defaultInstruments[tab];
      }
    },
  );
}
